/**
 * Rutas de la SPA (history mode; CloudFront responde index.html en 403/404 y el service worker
 * hace lo mismo sin red). Todo salvo /login exige sesion; `meta.admin` exige el grupo Cognito `admin`
 * (el backend lo vuelve a comprobar en cada ruta).
 */
import { nextTick } from "vue";
import { createRouter, createWebHistory } from "vue-router";

import { useAuthStore } from "@/stores/auth";

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    public?: boolean;
    admin?: boolean;
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("@/pages/LoginPage.vue"),
      meta: { title: "Entrar", public: true },
    },
    { path: "/", name: "library", component: () => import("@/pages/LibraryPage.vue"), meta: { title: "Biblioteca" } },
    {
      path: "/upload",
      name: "upload",
      component: () => import("@/pages/UploadPage.vue"),
      meta: { title: "Subir producto" },
    },
    {
      path: "/products/:id",
      name: "product",
      component: () => import("@/pages/ProductPage.vue"),
      props: true,
      meta: { title: "Producto" },
    },
    {
      path: "/settings",
      name: "settings",
      component: () => import("@/pages/SettingsPage.vue"),
      meta: { title: "Ajustes" },
    },
    {
      path: "/settings/prompts",
      name: "tenant-prompts",
      component: () => import("@/pages/prompts/PromptsIndexPage.vue"),
      props: { scope: "tenant" },
      meta: { title: "Prompts de tu tienda" },
    },
    {
      path: "/settings/prompts/:category/:promptKey",
      name: "tenant-prompt",
      component: () => import("@/pages/prompts/PromptEditorPage.vue"),
      props: (r) => ({ scope: "tenant", category: r.params.category, promptKey: r.params.promptKey }),
      meta: { title: "Prompt de tu tienda" },
    },
    {
      path: "/conversations",
      name: "conversations",
      component: () => import("@/pages/ConversationsPage.vue"),
      meta: { title: "Conversaciones" },
    },
    {
      path: "/conversations/:waId",
      name: "conversation",
      component: () => import("@/pages/ConversationPage.vue"),
      props: true,
      meta: { title: "Conversación" },
    },
    { path: "/admin", redirect: "/admin/prompts" },
    {
      path: "/admin/prompts",
      name: "admin-prompts",
      component: () => import("@/pages/prompts/PromptsIndexPage.vue"),
      props: { scope: "global" },
      meta: { title: "Prompts globales", admin: true },
    },
    {
      path: "/admin/prompts/:category/:promptKey",
      name: "admin-prompt",
      component: () => import("@/pages/prompts/PromptEditorPage.vue"),
      props: (r) => ({ scope: "global", category: r.params.category, promptKey: r.params.promptKey }),
      meta: { title: "Prompt global", admin: true },
    },
    {
      path: "/admin/lab",
      name: "lab",
      component: () => import("@/pages/admin/LabListPage.vue"),
      meta: { title: "Laboratorio", admin: true },
    },
    {
      path: "/admin/lab/new",
      name: "lab-new",
      component: () => import("@/pages/admin/LabNewPage.vue"),
      meta: { title: "Nueva prueba", admin: true },
    },
    {
      path: "/admin/lab/:id",
      name: "lab-test",
      component: () => import("@/pages/admin/LabTestPage.vue"),
      props: true,
      meta: { title: "Prueba", admin: true },
    },
    { path: "/:pathMatch(.*)*", redirect: "/" },
  ],
  scrollBehavior(to, from, saved) {
    if (saved) return saved;
    if (to.path === from.path) return false; // solo cambia la query (filtros de la biblioteca)
    return { top: 0 };
  },
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: "login", query: to.fullPath !== "/" ? { redirect: to.fullPath } : {} };
  }
  if (to.name === "login" && auth.isAuthenticated) return { path: "/" };
  if (to.meta.admin && !auth.isAdmin) return { path: "/" };
  return true;
});

router.afterEach((to, from) => {
  document.title = to.meta.title ? `${to.meta.title} · Kurioseando` : "Kurioseando Publisher";
  // Accesibilidad: al cambiar de pantalla el foco va al contenido principal (no al cambiar filtros).
  if (from.matched.length && to.path !== from.path) {
    void nextTick(() => document.getElementById("main")?.focus({ preventScroll: true }));
  }
});

export default router;
